/**
 * Hook pour la synchronisation automatique des templates
 * Détecte et synchronise automatiquement les nouveaux templates
 */

import { useState, useEffect, useCallback } from 'react';
import { useTemplateApi } from './useTemplateApi';
import { 
  detectTemplatesInFolder, 
  scanTemplatesInFolder,
  DetectedTemplate 
} from '../services/autoTemplateSync';
import { TemplateData } from '../services/templateService';

export const useAutoTemplateSync = () => {
  const { refreshTemplates, syncTemplates } = useTemplateApi();
  const [detectedTemplates, setDetectedTemplates] = useState<DetectedTemplate[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'detecting' | 'syncing' | 'completed' | 'error'>('idle');

  /**
   * Détecte les templates dans le dossier via l'API backend
   */
  const detectTemplates = useCallback(async () => {
    setIsDetecting(true);
    setSyncStatus('detecting');
    
    try {
      // Utiliser l'endpoint d'auto-détection du backend
      const detected = await detectTemplatesInFolder();
      setDetectedTemplates(detected || []);
      
      if (detected && detected.length > 0) {
        console.log(`🔄 ${detected.length} templates détectés et synchronisés automatiquement`);
        return detected;
      } else {
        console.log('✅ Aucun nouveau template détecté');
        return [];
      }
    } catch (error) {
      console.error('❌ Erreur détection templates:', error);
      setSyncStatus('error');
      return [];
    } finally {
      setIsDetecting(false);
    }
  }, []);

  /**
   * Synchronise automatiquement les nouveaux templates vers le backend
   * ACTIVE - Envoie les templates détectés au backend via l'API
   */
  const syncNewTemplates = useCallback(async (newTemplates: DetectedTemplate[]) => {
    if (newTemplates.length === 0) return;

    setSyncStatus('syncing');
    
    try {
      // Convertir DetectedTemplate[] en TemplateData[] pour la synchronisation
      const templatesToSync: TemplateData[] = newTemplates.map(t => ({
        template_id: t.template_id,
        component_name: t.component_name,
        file_path: t.file_path,
        category: t.category,
        industry: t.industry,
        style: t.style,
        name: t.name,
        description: '',
        tags: t.tags || [],
        is_active: t.is_active !== undefined ? t.is_active : true
      }));

      // Envoyer les templates au backend
      await syncTemplates(templatesToSync);
      
      setLastSyncTime(new Date());
      setSyncStatus('completed');
      
      console.log(`🎉 Synchronisation terminée: ${newTemplates.length} templates envoyés au backend`);
    } catch (error) {
      console.error('❌ Erreur synchronisation:', error);
      setSyncStatus('error');
      throw error;
    }
  }, [syncTemplates]);

  /**
   * Auto-sync complet: détection + synchronisation
   */
  const autoSync = useCallback(async () => {
    const newTemplates = await detectTemplates();
    if (newTemplates.length > 0) {
      await syncNewTemplates(newTemplates);
    }
  }, [detectTemplates, syncNewTemplates]);

  /**
   * Auto-sync au montage du composant
   */
  useEffect(() => {
    autoSync();
  }, [autoSync]);

  /**
   * Auto-sync périodique (optionnel)
   * DÉSACTIVÉ - Les templates sont locaux et ne changent pas, pas besoin de synchronisation périodique
   */
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     autoSync();
  //   }, 30000); // Toutes les 30 secondes
  //
  //   return () => clearInterval(interval);
  // }, [autoSync]);

  /**
   * Scanne les templates sans synchroniser (pour prévisualisation)
   */
  const scanTemplates = useCallback(async () => {
    setIsDetecting(true);
    setSyncStatus('detecting');
    
    try {
      const scanned = await scanTemplatesInFolder();
      setDetectedTemplates(scanned || []);
      
      console.log(`📁 ${scanned?.length || 0} templates trouvés dans le dossier`);
      return scanned || [];
    } catch (error) {
      console.error('❌ Erreur scan templates:', error);
      setSyncStatus('error');
      return [];
    } finally {
      setIsDetecting(false);
    }
  }, []);

  return {
    detectedTemplates,
    isDetecting,
    lastSyncTime,
    syncStatus,
    autoSync,
    detectTemplates,
    scanTemplates,
    syncNewTemplates
  };
};
